// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Pagination, { PaginationPresentationProps } from "@conklin/MobiusOverrides/Pagination";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes: Add new Fields
const enum fields {
    showResultsPerPage = "showResultsPerPage",
    showPagesNumber = "showPagesNumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showResultsPerPage]: boolean;
        [fields.showPagesNumber]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListsDataView: getWishListsDataView(state, state.pages.myLists.getWishListsParameter),
});

const mapDispatchToProps = {
    updateLoadParameter,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsPaginationStyles {
    wrapper?: InjectableCss;
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: MyListsPaginationStyles = {
    wrapper: {
        css: css`
            padding: 20px 0;
        `,
    },
};

const styles = paginationStyles;

const MyListsPagination: React.FC<Props> = ({
    wishListsDataView,
    updateLoadParameter,
    fields: { showResultsPerPage, showPagesNumber },
}) => {
    if (!wishListsDataView.value || !wishListsDataView.pagination) {
        return null;
    }

    const pagination = wishListsDataView.pagination;

    const changePage = (newPageIndex: number) => {
        updateLoadParameter({ page: newPageIndex });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateLoadParameter({ pageSize: newPageSize });
    };

    if (!pagination.totalItemCount) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="myLists_pagination">
            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                pageSizeCookie="Lists-PageSize"
                prevPage={pagination.prevPageUri}
                nextPage={pagination.nextPageUri}
                // XNG-Change
                showResultsPerPage={showResultsPerPage}
                showPagesNumber={showPagesNumber}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsPagination),
    definition: {
        group: "My Lists",
        displayName: "Pagination",
        allowedContexts: ["MyListsPage"],
        fieldDefinitions: [
            {
                name: fields.showResultsPerPage,
                displayName: "Show Results Per Page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPagesNumber,
                displayName: "Show Pages Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
